import { ActionButton } from '@/components/common/ActionButton';
import { TOOL_MANAGEMENT_DIALOG_PT } from '@/components/dialogs/dialogTheme';
import { copyToClipboard, downloadBlob } from '@/utils/download';
import { ArrowsIn, ArrowsOut, Copy, DownloadSimple } from '@phosphor-icons/react';
import { Dialog } from 'primereact/dialog';
import type { FC } from 'react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface TextViewDialogProps {
  visible: boolean;
  title: string;
  textContent: string;
  onClose: () => void;
  downloadFilename?: string;
}

/**
 * The content surface a text-ish dialog puts its payload on.
 *
 * 12 px radius inside the dialog's 16 px card: the nested step for the 24 px gutter the shared
 * dialog passthrough sets. Monospace is spent here because an APT source or an `.h` file is
 * machine output whose columns line up only in a fixed-width face.
 */
const VIEWER_SURFACE = 'overflow-auto rounded-xl bg-gray-50 p-4';
const VIEWER_TEXT = 'm-0 whitespace-pre-wrap break-words font-mono text-xs leading-5 text-gray-800';

export const TextViewDialog: FC<TextViewDialogProps> = ({
  visible,
  title,
  textContent,
  onClose,
  downloadFilename = 'file.txt',
}) => {
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleCopy = async () => {
    const success = await copyToClipboard(textContent);
    if (success) {
      toast.success(t('dialogs.copied'));
    } else {
      toast.error(t('dialogs.copyFailed'));
    }
  };

  const handleDownload = () => {
    try {
      const blob = new Blob([textContent], { type: 'text/plain' });
      downloadBlob(blob, downloadFilename);
      toast.success(t('dialogs.downloaded'));
    } catch (_error) {
      toast.error(t('dialogs.downloadFailed'));
    }
  };

  const fullscreenLabel = isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen');

  const dialogHeader = (
    <div className="flex w-full min-w-0 items-center justify-between gap-4 pr-2">
      <span className="min-w-0 truncate text-lg font-semibold text-gray-900">{title}</span>
      <span className="flex shrink-0 items-center gap-1">
        <ActionButton
          variant="ghost"
          iconOnly={true}
          icon={<Copy size={16} aria-hidden="true" />}
          onClick={handleCopy}
          title={t('common.copy')}
          aria-label={t('common.copy')}
        />
        <ActionButton
          variant="ghost"
          iconOnly={true}
          icon={<DownloadSimple size={16} aria-hidden="true" />}
          onClick={handleDownload}
          title={t('common.download')}
          aria-label={t('common.download')}
        />
        <ActionButton
          variant="ghost"
          iconOnly={true}
          icon={isFullscreen ? <ArrowsIn size={16} aria-hidden="true" /> : <ArrowsOut size={16} aria-hidden="true" />}
          onClick={() => setIsFullscreen((prev) => !prev)}
          title={fullscreenLabel}
          aria-label={fullscreenLabel}
        />
      </span>
    </div>
  );

  const dialogFooter = (
    <ActionButton variant="ghost" onClick={onClose}>
      {t('common.close')}
    </ActionButton>
  );

  return (
    <Dialog
      header={dialogHeader}
      footer={dialogFooter}
      visible={visible}
      onHide={onClose}
      modal={true}
      className={`w-full ${isFullscreen ? 'max-w-full h-full' : 'max-w-3xl'}`}
      dismissableMask={true}
      style={
        isFullscreen ? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', margin: 0 } : undefined
      }
      pt={{
        ...TOOL_MANAGEMENT_DIALOG_PT,
        root: {
          className: isFullscreen
            ? 'border border-gray-200 rounded-2xl shadow-lg fixed inset-0 h-full m-0'
            : TOOL_MANAGEMENT_DIALOG_PT.root.className,
        },
        content: {
          className: `${TOOL_MANAGEMENT_DIALOG_PT.content.className} ${isFullscreen ? 'h-full flex flex-col' : ''}`,
        },
      }}
    >
      <div className={`flex flex-col ${isFullscreen ? 'h-full' : ''}`}>
        <div className={`${VIEWER_SURFACE} ${isFullscreen ? 'flex-1' : 'max-h-96'}`}>
          <pre className={VIEWER_TEXT}>{textContent}</pre>
        </div>
      </div>
    </Dialog>
  );
};
